import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  Edit, 
  Trash2, 
  Calendar, 
  MapPin, 
  Users, 
  UserPlus,
  CheckSquare,
  Truck,
  Sparkles,
  ExternalLink,
  LayoutGrid,
  X,
  Monitor,
  Check,
  Shirt
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import { PageContainer } from '@/components/layout/PageContainer';
import { PageState } from '@/components/layout/PageState';
import { eventService } from '@/lib/api/event.service';
import { guestService } from '@/lib/api/guest.service';
import { taskService } from '@/lib/api/task.service';
import { serviceService } from '@/lib/api/service.service';
import { venueService } from '@/lib/api/venue.service';
import { venueRoomService } from '@/lib/api/venue-room.service';
import { celebrationService } from '@/lib/api/celebration.service';
import { ActivitiesManager } from '@/components/activities/ActivitiesManager';
import { EventTemplatePicker } from '@/components/events/EventTemplatePicker';
import { GoalsCard } from '@/components/goals';
import { HeroQuickMenuCard } from '@/components/shared/HeroQuickMenuCard';
import { formatEventDateTime, formatEventDateRange } from '@/utils/dateFormat';
import type { Event, EventMember } from '@/types/event.types';
import type { Guest } from '@/types/guest.types';
import type { Task } from '@/types/task.types';
import type { Service } from '@/types/service.types';
import type { VenueWithGoogleDetails } from '@/types/venue.types';
import type { VenueRoomWithConfigs } from '@/types/venue-room.types';
import { ROOM_TYPE_LABELS, SEATING_CONFIG_LABELS, SeatingConfigType } from '@/types/venue-room.types';
import type { Celebration } from '@/types/celebration.types';

/** Read-only room card for display and selection */
export const RoomCard: React.FC<{
  room: VenueRoomWithConfigs;
  isAssigned?: boolean;
  showSelectButton?: boolean;
  onSelect?: (roomId: number, config?: string) => void;
  onRemove?: () => void;
  onChangeSeatingConfig?: (config: string) => void;
  disabled?: boolean;
  seatingConfig?: SeatingConfigType;
}> = ({ room, isAssigned, showSelectButton, onSelect, onRemove, onChangeSeatingConfig, disabled, seatingConfig }) => {
  const [selectedSeatingConfig, setSelectedSeatingConfig] = React.useState<string | undefined>(
    room.seating_configs?.find(sc => sc.is_default)?.config_type
  );

  return (
  <div className="rounded-lg border p-3 transition-colors hover:bg-muted/30">
    <div className="flex items-start justify-between gap-2">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-semibold text-sm">{room.name}</h3>
          <Badge variant="outline" className="text-[10px] px-1.5 py-0">
            {ROOM_TYPE_LABELS[room.room_type] || room.room_type}
          </Badge>
        </div>

        {room.description && (
          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{room.description}</p>
        )}

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-xs text-muted-foreground">
          {room.max_capacity && (
            <span className="flex items-center gap-1">
              <Users className="h-3 w-3" />
              Up to {room.max_capacity}
            </span>
          )}
          {room.floor_area_sqft && (
            <span>{room.floor_area_sqft.toLocaleString()} sq ft</span>
          )}
          {room.has_av_equipment && (
            <span className="flex items-center gap-1">
              <Monitor className="h-3 w-3" />
              AV
            </span>
          )}
          {room.has_stage && <span>Stage</span>}
          {room.has_dance_floor && <span>Dance Floor</span>}
          {room.is_accessible && <span>Accessible</span>}
        </div>

        {/* Active seating config badge for assigned room */}
        {isAssigned && seatingConfig && (
          <div className="mt-1.5">
            <Badge variant="secondary" size="sm" className="text-[10px]">
              {SEATING_CONFIG_LABELS[seatingConfig as keyof typeof SEATING_CONFIG_LABELS] || seatingConfig}
            </Badge>
          </div>
        )}

        {/* Seating config selector for picker mode - Badge style */}
        {!isAssigned && room.seating_configs.length > 0 && showSelectButton && (
          <div className="mt-2.5">
            <label className="text-xs font-medium text-muted-foreground block mb-1.5">
              Seating Layout {!selectedSeatingConfig && <span className="text-destructive">*</span>}
            </label>
            <div className="flex flex-wrap gap-1.5">
              {room.seating_configs.map(sc => (
                <Badge
                  key={sc.config_type}
                  variant={selectedSeatingConfig === sc.config_type ? 'default' : 'outline'}
                  className={`text-[11px] px-2 py-0.5 cursor-pointer transition-all ${
                    selectedSeatingConfig === sc.config_type
                      ? 'font-medium'
                      : 'hover:bg-primary hover:text-primary-foreground hover:border-primary'
                  }`}
                  onClick={() => setSelectedSeatingConfig(sc.config_type)}
                  data-testid={`seating-config-badge-${room.id}-${sc.config_type}`}
                >
                  {selectedSeatingConfig === sc.config_type && <Check className="h-3 w-3 mr-1" />}
                  {SEATING_CONFIG_LABELS[sc.config_type]}
                  {sc.capacity ? ` (${sc.capacity})` : ''}
                  {sc.is_default && !selectedSeatingConfig ? ' ★' : ''}
                </Badge>
              ))}
            </div>
            {!selectedSeatingConfig && (
              <p className="text-[10px] text-muted-foreground mt-1">Click a layout to select</p>
            )}
          </div>
        )}

        {/* Warning for rooms without seating configs */}
        {!isAssigned && room.seating_configs.length === 0 && showSelectButton && (
          <div className="mt-2 flex items-start gap-1.5 px-2 py-1.5 bg-amber-50 border border-amber-200 rounded text-[11px] text-amber-700">
            <span>⚠️</span>
            <span>No seating layouts configured. Cannot be used.</span>
          </div>
        )}

        {/* Seating configs list (shown when assigned with change handler) */}
        {isAssigned && !showSelectButton && onChangeSeatingConfig && room.seating_configs.length > 0 && (
          <div className="mt-2">
            {room.seating_configs.filter(sc => sc.config_type !== seatingConfig).length > 0 && (
              <div>
                <span className="text-[10px] text-muted-foreground uppercase tracking-wide font-medium block mb-1">
                  Other Available (click to switch)
                </span>
                <div className="flex flex-wrap gap-1">
                  {room.seating_configs
                    .filter(sc => sc.config_type !== seatingConfig)
                    .map(sc => (
                      <Badge
                        key={sc.config_type}
                        variant="outline"
                        className="text-[10px] px-1.5 py-0 font-normal text-muted-foreground cursor-pointer hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors"
                        onClick={!disabled ? () => onChangeSeatingConfig(sc.config_type) : undefined}
                      >
                        {SEATING_CONFIG_LABELS[sc.config_type]}
                        {sc.capacity ? ` (${sc.capacity})` : ''}
                      </Badge>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center gap-1 flex-shrink-0">
        {/* Remove button (X icon) for assigned room */}
        {isAssigned && onRemove && (
          <button
            type="button"
            onClick={onRemove}
            disabled={disabled}
            className="p-1 rounded-md text-muted-foreground/50 hover:text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50"
            title="Remove room assignment"
          >
            <X className="h-4 w-4" />
          </button>
        )}

        {/* Select button for picker mode */}
        {showSelectButton && onSelect && (
          <Button
            variant="outline"
            size="xs"
            onClick={() => onSelect(room.id, selectedSeatingConfig)}
            disabled={disabled || !selectedSeatingConfig}
            data-testid={`select-room-btn-${room.id}`}
          >
            Select
          </Button>
        )}
      </div>
    </div>
  </div>
  );
};

export const EventDetails: React.FC = () => {
  const { t } = useTranslation(['events', 'common']);
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState<Event | null>(null);
  const [celebration, setCelebration] = useState<Celebration | null>(null);
  const [members, setMembers] = useState<EventMember[]>([]);
  const [venue, setVenue] = useState<VenueWithGoogleDetails | null>(null);
  const [venueRoom, setVenueRoom] = useState<VenueRoomWithConfigs | null>(null);
  const [venueRooms, setVenueRooms] = useState<VenueRoomWithConfigs[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isPromoting, setIsPromoting] = useState(false);
  const [promotionError, setPromotionError] = useState<string | null>(null);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [statistics, setStatistics] = useState({
    totalGuests: 0,
    confirmedRSVPs: 0,
    declinedRSVPs: 0,
    invitedRSVPs: 0,
    newRSVPs: 0,
    totalTasks: 0,
    pendingTasks: 0,
    completedTasks: 0,
    totalServices: 0,
    confirmedServices: 0,
    teamMembers: 0,
    totalActivities: 0
  });

  const handleActivitiesLoaded = useCallback((count: number) => {
    setStatistics((prev) => {
      if (prev.totalActivities === count) {
        return prev;
      }

      return { ...prev, totalActivities: count };
    });
  }, []);

  useEffect(() => {
    if (id) loadEventData(parseInt(id));
  }, [id]);

  const loadEventData = async (eventId: number) => {
    try {
      setLoading(true);
      const [eventData, membersData] = await Promise.all([
        eventService.getEvent(eventId),
        eventService.getEventMembers(eventId)
      ]);
      setEvent(eventData);
      setMembers(membersData);
      setStatistics(prev => ({ ...prev, teamMembers: membersData.length }));
      
      // Load celebration for breadcrumb
      if (eventData.celebration_id) {
        try {
          const celebrationData = await celebrationService.getCelebration(eventData.celebration_id);
          setCelebration(celebrationData);
        } catch (err) {
          console.error('Failed to load celebration:', err);
        }
      }
      
      if (eventData.venue_id) {
        try {
          const venueData = await venueService.getVenueWithDetails(eventData.venue_id);
          setVenue(venueData);

          // Load all rooms for the venue (for the room selector)
          try {
            const rooms = await venueRoomService.getRooms(eventData.venue_id);
            setVenueRooms(rooms);
          } catch (roomsErr) {
            console.error('Failed to load venue rooms:', roomsErr);
            setVenueRooms([]);
          }

          // Load room info if event has a venue_room_id
          if (eventData.venue_room_id) {
            try {
              const roomData = await venueRoomService.getRoom(eventData.venue_id, eventData.venue_room_id);
              setVenueRoom(roomData);
            } catch (roomErr) {
              console.error('Failed to load venue room:', roomErr);
            }
          }
        } catch (venueError) {
          console.error('Failed to load venue:', venueError);
        }
      }
      
      await loadStatistics(eventId, membersData);
      setError(null);
    } catch (err) {
      setError('Failed to load event');
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async (eventId: number, eventMembers?: EventMember[]) => {
    try {
      const [guests, tasks, services] = await Promise.all([
        guestService.getEventGuests(eventId, true),
        taskService.getEventTasks(eventId),
        serviceService.getEventServices(eventId)
      ]);

      setStatistics((prev) => ({
        ...prev,
        totalGuests: guests.length,
        confirmedRSVPs: guests.filter((g: Guest) => g.rsvp_status === 'accepted').length,
        declinedRSVPs: guests.filter((g: Guest) => g.rsvp_status === 'declined').length,
        invitedRSVPs: guests.filter((g: Guest) => g.rsvp_status === 'invited').length,
        newRSVPs: guests.filter((g: Guest) => g.rsvp_status === 'new').length,
        totalTasks: tasks.length,
        pendingTasks: tasks.filter((t: Task) => t.status === 'pending').length,
        completedTasks: tasks.filter((t: Task) => t.status === 'completed').length,
        totalServices: services.length,
        confirmedServices: services.filter((s: Service) => s.status === 'confirmed').length,
        teamMembers: eventMembers ? eventMembers.length : members.length
      }));
    } catch (err) {
      console.error('Error loading statistics:', err);
    }
  };

  const handleDeleteEvent = async () => {
    if (!event || !confirm('Delete this event?')) return;

    try {
      setDeleteLoading(true);
      await eventService.deleteEvent(event.id);
      if (event.celebration_id) {
        navigate(`/celebrations/${event.celebration_id}`);
      } else {
        navigate('/events');
      }
    } catch (err) {
      setError('Failed to delete event');
      setDeleteLoading(false);
    }
  };

  const promotionTargetStatus = (() => {
    switch (event?.status) {
      case 'draft':
        return 'planning' as const;
      case 'planning':
        return 'planned' as const;
      case 'planned':
        return 'active' as const;
      case 'active':
        return 'completed' as const;
      default:
        return null;
    }
  })();

  const formatStatusLabel = (status?: string): string => {
    if (!status) return 'draft';
    return status.replace(/_/g, ' ');
  };

  const handlePromoteEvent = async () => {
    if (!event || !promotionTargetStatus) return;

    try {
      setIsPromoting(true);
      setPromotionError(null);
      await eventService.transitionEventStatus(event.id, promotionTargetStatus);
      await loadEventData(event.id);
    } catch (err) {
      const apiError = err as { response?: { data?: { message?: string } } };
      setPromotionError(apiError?.response?.data?.message || 'Failed to promote event status.');
    } finally {
      setIsPromoting(false);
    }
  };

  if (loading) {
    return (
      <PageState
        kind="loading"
        width="default"
        title="Loading event"
        description="Please wait while we load event details."
      />
    );
  }

  if (!event) {
    return (
      <PageState
        kind="empty"
        width="default"
        title="Event not found"
        description="The requested event may have been removed or is unavailable."
        action={
          <Link to="/events">
            <Button size="sm">Back to Events</Button>
          </Link>
        }
      />
    );
  }

  const eventDateLabel = event.start_time && event.end_time
    ? formatEventDateRange(event.start_time, event.end_time)
    : formatEventDateTime(event.event_date, true);

  const backTo = event.celebration_id ? `/celebrations/${event.celebration_id}` : '/events';

  return (
    <PageContainer width="wide" className="space-y-4">
      <AppBreadcrumbs
        items={
          celebration
            ? [
                { label: t('common:nav.home'), to: '/home' },
                { label: t('common:nav.celebrations'), to: '/celebrations' },
                { label: celebration.title, to: `/celebrations/${celebration.id}` },
                { label: event.title },
              ]
            : event.celebration_id
              ? [
                  { label: t('common:nav.home'), to: '/home' },
                  { label: t('common:nav.celebrations'), to: '/celebrations' },
                  { label: 'Celebration', to: `/celebrations/${event.celebration_id}` },
                  { label: event.title },
                ]
              : [
                  { label: t('common:nav.home'), to: '/home' },
                  { label: t('common:nav.events'), to: '/events' },
                  { label: event.title },
                ]
        }
      />

      <HeroQuickMenuCard
        borderClassName="border-primary/20"
        heroClassName="bg-gradient-to-br from-primary/10 via-primary/[0.04] to-white"
        heroContent={
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-primary/10 ring-2 ring-primary/20 flex items-center justify-center flex-shrink-0 shadow-sm">
              <Calendar className="h-7 w-7 text-primary" />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <h1 className="text-xl font-bold tracking-tight text-foreground">{event.title}</h1>
                    <Badge variant="outline" size="sm" className="capitalize">{event.event_type.replace(/_/g, ' ')}</Badge>
                    {!event.is_public && <Badge variant="outline" size="sm">Private</Badge>}
                  </div>

                  <div className="mt-1 inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white/80 px-3 py-1">
                    <span className="text-[11px] uppercase tracking-wide text-slate-500">Current status</span>
                    <span className="text-sm font-semibold text-slate-900 capitalize">{formatStatusLabel(event.status)}</span>
                  </div>

                  <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground flex-wrap">
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      {eventDateLabel}
                    </span>
                    {event.max_guests ? (
                      <span className="inline-flex items-center gap-1 font-medium text-foreground/70">
                        <Users className="h-3 w-3" />
                        Max {event.max_guests}
                      </span>
                    ) : null}
                    {event.venue_name || venue?.name ? (
                      <span className="inline-flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {event.venue_name || venue?.name}
                      </span>
                    ) : null}
                  </div>

                  {event.description && (
                    <p className="text-xs text-muted-foreground mt-1.5 line-clamp-1">{event.description}</p>
                  )}
                </div>

                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {promotionTargetStatus && (
                    <Button
                      size="sm"
                      className="h-9 px-4 bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm"
                      onClick={handlePromoteEvent}
                      disabled={isPromoting}
                    >
                      <Sparkles className="h-4 w-4 mr-1.5" />
                      {isPromoting ? 'Promoting…' : `Promote to ${formatStatusLabel(promotionTargetStatus)}`}
                    </Button>
                  )}
                  <Link to={backTo}>
                    <Button variant="ghost" size="xs">{t('common:actions.back')}</Button>
                  </Link>
                  <Link to={`/events/${event.id}/edit`}>
                    <Button variant="ghost" size="icon-sm" title="Edit">
                      <Edit className="h-4 w-4" />
                    </Button>
                  </Link>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={handleDeleteEvent}
                    disabled={deleteLoading}
                    className="text-destructive hover:bg-destructive/10"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        }
        items={[
          {
            key: 'guests',
            icon: Users,
            iconClassName: 'text-blue-500',
            hoverClassName: 'hover:bg-blue-50',
            value: statistics.totalGuests,
            label: t('common:nav.guests'),
            href: `/events/${event.id}/guests`,
          },
          {
            key: 'tasks',
            icon: CheckSquare,
            iconClassName: 'text-amber-500',
            hoverClassName: 'hover:bg-amber-50',
            value: `${statistics.pendingTasks}/${statistics.totalTasks}`,
            label: 'Tasks Left',
            href: `/events/${event.id}/tasks`,
          },
          {
            key: 'services',
            icon: Truck,
            iconClassName: 'text-purple-500',
            hoverClassName: 'hover:bg-purple-50',
            value: `${statistics.confirmedServices}/${statistics.totalServices}`,
            label: t('common:nav.services'),
            href: `/events/${event.id}/services`,
          },
          {
            key: 'activities',
            icon: Calendar,
            iconClassName: 'text-emerald-500',
            hoverClassName: 'hover:bg-emerald-50',
            value: statistics.totalActivities,
            label: t('common:nav.activities'),
          },
          {
            key: 'team',
            icon: UserPlus,
            iconClassName: 'text-teal-500',
            hoverClassName: 'hover:bg-teal-50',
            value: statistics.teamMembers,
            label: t('common:nav.team'),
            href: `/events/${event.id}/add-member`,
          },
          {
            key: 'dress-code',
            icon: Shirt,
            iconClassName: 'text-indigo-500',
            hoverClassName: 'hover:bg-indigo-50',
            value: event.dress_code_enabled ? 'Enabled' : 'Off',
            label: 'Dress Code',
            href: event.celebration_id ? `/celebrations/${event.celebration_id}/events/${event.id}/dress-code` : undefined,
          },
        ]}
      />

      {promotionError && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="py-3 text-sm text-amber-900">{promotionError}</CardContent>
        </Card>
      )}

        {/* Venue + Assigned Room card */}
        {venue && (venue.name || venue.formatted_address) && (
          <Card className="mt-3">
            <CardContent className="pt-4 pb-4 space-y-3">
              {/* Venue name + address */}
              <div className="flex items-start justify-between gap-2">
                <div className="space-y-0.5">
                  {venue.name && (
                    event.venue_id ? (
                      <Link
                        to={event.celebration_id ? `/celebrations/${event.celebration_id}/venues/${event.venue_id}` : `/venues/${event.venue_id}`}
                        className="font-semibold text-sm text-foreground inline-flex items-center gap-1 hover:text-primary hover:underline"
                      >
                        <MapPin className="h-4 w-4" />
                        {venue.name}
                      </Link>
                    ) : (
                      <p className="font-semibold text-sm text-foreground inline-flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        {venue.name}
                      </p>
                    )
                  )}
                  {venue.formatted_address && (
                    <p className="text-xs text-muted-foreground pl-5">{venue.formatted_address}</p>
                  )}
                </div>
              </div>

              {/* Room — read-only view */}
              {venueRoom && (
                <div className="border-t pt-3">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <LayoutGrid className="h-4 w-4 text-muted-foreground" />
                      <span className="font-semibold text-sm">{venueRoom.name}</span>
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                        {ROOM_TYPE_LABELS[venueRoom.room_type] || venueRoom.room_type}
                      </Badge>
                      {event.seating_config && (
                        <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                          {SEATING_CONFIG_LABELS[event.seating_config as keyof typeof SEATING_CONFIG_LABELS] || event.seating_config}
                        </Badge>
                      )}
                    </div>
                    <Link
                      to={`/events/${event.id}/seating`}
                      className="text-xs text-muted-foreground hover:text-primary transition-colors font-medium"
                    >
                      Change
                    </Link>
                  </div>
                </div>
              )}

              {/* No room assigned — prompt to seating setup */}
              {!venueRoom && venueRooms.length > 0 && (
                <div className="border-t pt-3">
                  <Link
                    to={`/events/${event.id}/seating`}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-dashed border-muted-foreground/30 text-sm text-muted-foreground transition-colors hover:bg-muted/50 hover:border-primary/40 hover:text-primary"
                  >
                    <LayoutGrid className="h-4 w-4" />
                    Assign Room &amp; Seating
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>
        )}

      {/* No venue or no rooms - prompt to set up */}
      {event.venue_id && venue && venueRooms.length === 0 && (
        <div className="bg-blue-50 rounded-lg border border-blue-200 p-3">
          <div className="flex items-start gap-2">
            <LayoutGrid className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-blue-900">No rooms available for this venue</p>
              <p className="text-xs text-blue-700 mt-0.5">Add event spaces to assign a room to this event.</p>
              <div className="flex flex-col gap-1 mt-1.5">
                <Link
                  to={event.celebration_id ? `/celebrations/${event.celebration_id}/venues/${event.venue_id}` : `/venues/${event.venue_id}`}
                  className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline"
                >
                  Manage Venue Rooms <ExternalLink className="h-3 w-3" />
                </Link>
                <Link
                  to={`/events/${event.id}/seating`}
                  className="inline-flex items-center gap-1 text-xs font-medium text-amber-600 hover:text-amber-800 hover:underline"
                >
                  Seating Setup <LayoutGrid className="h-3 w-3" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
          {error}
        </div>
      )}

      {/* Goals Section */}
      <GoalsCard entityType="event" entityId={event.id} />

      {/* Activities Section */}
      <div className="bg-white rounded-lg border p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-lg">Activities</h2>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowTemplatePicker(true)}
            className="flex items-center gap-2"
          >
            <Sparkles className="h-4 w-4" />
            Add activities
          </Button>
        </div>
        <ActivitiesManager
          eventId={event.id}
          canManage={true}
          onActivitiesLoaded={handleActivitiesLoaded}
        />
      </div>

      {/* Event Template Picker Modal */}
      {showTemplatePicker && (
        <EventTemplatePicker
          open={showTemplatePicker}
          onClose={() => setShowTemplatePicker(false)}
          entityType="event"
          entityId={event.id}
          onApplied={() => {
            loadEventData(event.id); // Reload to show new activities
          }}
        />
      )}

      {/* Team Members - Compact */}
      {members.length > 0 && (
        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="font-semibold text-sm">Event Team</span>
            <Link to={`/events/${event.id}/add-member`}>
              <Button variant="ghost" size="xs">
                <UserPlus className="h-3 w-3 mr-1" />Add
              </Button>
            </Link>
          </div>
          <div className="flex flex-wrap gap-2">
            {members.map((member) => (
              <div key={member.id} className="inline-flex items-center gap-1.5 px-2 py-1 bg-muted/50 rounded-full text-xs">
                <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center text-[10px] font-bold text-primary">
                  {(member.user?.full_name || member.user?.username || '?').charAt(0)}
                </div>
                <span className="font-medium">{member.user?.full_name || member.user?.username}</span>
                <span className="text-muted-foreground capitalize">· {member.role.replace('_', ' ')}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Event contextual actions removed - use AI Assistant sidebar for notifications */}
    </PageContainer>
  );
};
